import { useCallback, useState } from 'react'
import { useAuthStore } from 'stores/auth'
import type { User } from 'types/users'
import type { MutationResult } from 'utils/api'

import { useFetchApi } from '../useFetchApi'

export interface UpdateProfilePayload {
  name?: string
  picture?: string | null
}

/**
 * Update the current user's profile (name / picture). On success, writes the
 * returned user back into the auth store so the sidebar + other consumers
 * pick up the new values without a round-trip through `fetchUser`.
 */
export function useUpdateMe(): MutationResult<User, [UpdateProfilePayload]> {
  const { fetchApi } = useFetchApi()
  const setUser = useAuthStore((s) => s.setUser)
  const [loading, setLoading] = useState(false)

  const perform = useCallback(
    async (payload: UpdateProfilePayload): Promise<User> => {
      setLoading(true)
      try {
        const user = await fetchApi<User>('/api/users/me', {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
        setUser(user)
        return user
      } finally {
        setLoading(false)
      }
    },
    [fetchApi, setUser],
  )

  return { perform, loading }
}
